/**
 * @fileoverview Arc variant of `EvaluativeItem`. Adds the AR-535 drawer tweaks
 * (lv2/lv3 breadcrumb info on the curriculum item header, plus a "rubric for
 * selected score" viewer) on top of the legacy behavior. Legacy component is
 * left untouched so the SessionMeetingAssessment page is unaffected.
 */

import { useState } from 'react'
import {
  WrapperEvaluate,
  ScoreContainer,
} from '@legacy/pages/SessionMeetingAssessment/components/StudentModalContent/StudentModalContentStyled'
import { ScoreCard } from 'components/ScoreComponent/ScoreCard'
import times from 'lodash/times'
import {
  IStudentAsssessmentsprops,
} from '@legacy/pages/SessionMeetingAssessment/components/StudentModalContent/EvaluativeItemTypes'
import { useArcTranslation } from 'hooks'
import { ScoreItemLabel } from 'components/ScoreComponent/ScoreItem'
import { Stack, Text } from 'components'

const RUBRIC_KEYS = ['rubric_1', 'rubric_2', 'rubric_3', 'rubric_4', 'rubric_5']
const SCORE_COUNT = 5

/** Returns the rubric description for a given 1..5 score, falling back to the
 * default i18n rubric text when the item-specific rubric is empty. */
const pickRubric = (
  scoreIndex: number,
  itemRubrics: any,
  defaultRubrics: string[],
): string => {
  const fromItem = itemRubrics?.[scoreIndex + 1]
  if (fromItem && fromItem !== '') return fromItem
  return defaultRubrics[scoreIndex] ?? ''
}

// `setData` is omitted: scores are autosaved one by one, so there is no batch
// payload to accumulate for a submit anymore (AR-828).
type ArcEvaluativeItemProps = Omit<IStudentAsssessmentsprops, 'setData'> & {
  // Persist the student's score as soon as it is picked (AR-771 autosave).
  onAutosave?: (id: string, studentScore: number) => void
}

export const ArcEvaluativeItem = ({
  item,
  mixPanelTracker,
  setHasChanges,
  onAutosave,
}: ArcEvaluativeItemProps) => {
  // Student self-assessment must reflect only the student's own score. `0` means
  // "not assessed" — do not fall back to the teacher's masterScore (AR-770).
  const [score, setScore] = useState((item as any).studentScore ?? 0)
  const { t } = useArcTranslation()

  const defaultModalRubrics = RUBRIC_KEYS.map(key => t(key))

  // Extract lv2/lv3 from breadcrumb when the curriculum item carries one.
  // Backend returns the format "lv1 > lv2 > lv3 > leaf"; older payloads expose
  // explicit `lv2Name` / `lv3Name` fields, so prefer those when present.
  const itemAny = item as any
  const breadcrumbParts =
    typeof itemAny.breadcrumb === 'string'
      ? itemAny.breadcrumb.split('>').map((s: string) => s.trim()).filter(Boolean)
      : []
  const lv2Name = itemAny.lv2Name || breadcrumbParts[1] || undefined
  const lv3Name = itemAny.lv3Name || breadcrumbParts[2] || undefined

  const onClickScore = async (i: number) => {
    mixPanelTracker('Definir número avaliação')
    const scorepayload = score === i + 1 ? 0 : i + 1
    setScore(scorepayload)
    setHasChanges(true)
    onAutosave?.(item.id, scorepayload)
  }

  const selectedRubric = score > 0 ? pickRubric(score - 1, item.rubrics, defaultModalRubrics) : null

  return (
    <WrapperEvaluate>
      {/* Same header as My tasks (AssessmentScoreItem): the curriculum color
          lives only on the card's left border, never behind text (AR-919). */}
      <Stack spacing={2}>
        <Text size='xs' color='dimmed'>
          {[item.lv1Name, lv2Name, lv3Name].filter(Boolean).join(' › ')}
        </Text>
        <Text size='sm' weight={500}>
          {item.originalCodes && (
            <Text span weight={700} mr={6}>
              {item.originalCodes}
            </Text>
          )}
          {item.name}
        </Text>
      </Stack>
      <Stack spacing='md' mt='md' align='flex-start'>
        <ScoreContainer>
          {times(SCORE_COUNT, i => (
            <ScoreCard
              label={(i + 1) as ScoreItemLabel}
              key={i}
              active={score === i + 1}
              rubric={pickRubric(i, item.rubrics, defaultModalRubrics)}
              onClick={() => onClickScore(i)}
            />
          ))}
        </ScoreContainer>
        {selectedRubric && (
          <Text size='sm' italic color='dimmed'>
            {score} — {selectedRubric}
          </Text>
        )}
      </Stack>
    </WrapperEvaluate>
  )
}
